import React, { useState } from 'react'
import {
  TableView,
  TableHeader,
  Column,
  TableBody,
  Row,
  Cell,
  StatusLight,
  Text,
  Button,
  DialogTrigger
} from '@adobe/react-spectrum'
import JobPreviewDialog from './JobPreviewDialog'

interface Job {
  id: string
  name: string
  status: 'completed' | 'in-progress' | 'pending' | 'failed'
  createdDate?: string
  completedDate?: string
  device?: string
  deviceType?: string
  totalTests?: number
  passedTests?: number
  failedTests?: number
  engineer?: string
}

interface RecentJobsTableSpectrumProps {
  jobs: Job[]
  translate: (key: string) => string
}

const RecentJobsTable: React.FC<RecentJobsTableSpectrumProps> = ({ jobs, translate }) => {
  const [previewJobId, setPreviewJobId] = useState<string | null>(null)
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false)

  const statusMap: Record<string, any> = {
    completed: 'positive',
    'in-progress': 'notice',
    pending: 'neutral',
    failed: 'negative'
  }

  const getLocale = () => window.localStorage.getItem('language') || 'en-GB'

  const formatDate = (dateString?: string): string => {
    if (!dateString) return translate('not_available')
    try {
      return new Date(dateString).toLocaleDateString(getLocale(), {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    } catch {
      return dateString
    }
  }

  const formatDateTime = (dateString?: string): string => {
    if (!dateString) return translate('not_available')
    try {
      const locale = getLocale()
      const date = new Date(dateString)
      const dateFormatted = date.toLocaleDateString(locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
      const timeFormatted = date.toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      })
      return `${dateFormatted} ${timeFormatted}`
    } catch {
      return dateString
    }
  }

  const handleOpenJob = (jobId: string) => {
    window.location.href = `/job/${jobId}`
  }

  return (
    <>
      <TableView
        aria-label="Recent jobs table"
        selectionMode="multiple" selectionStyle="highlight"
        UNSAFE_className="home-table"
        UNSAFE_style={{ width: '100%' }}
      >
        <TableHeader>
          <Column key="name" allowsSorting width="1fr">
            {translate('job_name_header')}
          </Column>
          <Column key="status" allowsSorting width={210}>
            {translate('test_results_header')}
          </Column>
          <Column key="device" width={200}>
            {translate('device_header')}
          </Column>
          <Column key="completedDate" allowsSorting width={160}>
            {translate('completed_header')}
          </Column>
          <Column key="actions" width={330} align="center">
            {translate('actions_header')}
          </Column>
        </TableHeader>
        <TableBody>
          {jobs.map((job: Job) => (
            <Row key={job.id}>
              <Cell>
                <Text>{job.name}</Text>
              </Cell>
              <Cell>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <Text UNSAFE_style={{ fontSize: '12px', color: '#333', fontWeight: 500 }}>
                    {job.totalTests || 0}
                  </Text>
                  <StatusLight variant="positive" />
                  <Text UNSAFE_style={{ fontSize: '12px', color: '#22c55e' }}>
                    {job.passedTests || 0}
                  </Text>
                  <StatusLight variant="negative" />
                  <Text UNSAFE_style={{ fontSize: '12px', color: '#ef4444' }}>
                    {job.failedTests || 0}
                  </Text>
                </div>
              </Cell>
              <Cell>
                <Text>{job.device ? translate(job.device) : translate('lantekIII')}</Text>
              </Cell>
              <Cell>
                <Text>{formatDateTime(job.completedDate)}</Text>
              </Cell>
            <Cell>
              <div className="home-table-actions">
                <Button variant="secondary" UNSAFE_className="home-table-action home-table-action-view" onPress={() => handleOpenJob(job.id)}>
                  {translate('general_view')}
                </Button>
                <Button
                  variant="secondary"
                  UNSAFE_className="home-table-action home-table-action-preview"
                  onPress={() => {
                    setPreviewJobId(job.id)
                    setPreviewDialogOpen(true)
                  }}
                >
                  {translate('preview')}
                </Button>
              </div>
            </Cell>
          </Row>
        ))}
      </TableBody>
    </TableView>
    {previewJobId && (
      <DialogTrigger
        type="modal"
        isOpen={previewDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setPreviewDialogOpen(false)
            setPreviewJobId(null)
          }
        }}
      >
        <div style={{ display: 'none' }} aria-hidden="true" />
        <JobPreviewDialog
          jobId={previewJobId}
          isOpen={previewDialogOpen}
          onClose={() => {
            setPreviewDialogOpen(false)
            setPreviewJobId(null)
          }}
          translate={translate}
        />
      </DialogTrigger>
    )}
    </>
  )
}

export default RecentJobsTable
